import { useFormik } from "formik";
import { FormValues, LayoutUserDateProps } from "./type";
import { validationSchema } from "./schema";
import { Formatter } from "utils/formatters";
import toast from "react-hot-toast";
import {
  FaRegEye as EyeOpen,
  FaRegEyeSlash as EyeClosed,
} from "react-icons/fa";
import { ModalPassword } from "./modal-password";
import { ModalEmail } from "./modal-email";
import { useEffect, useState } from "react";
import { ChangeUserData, changeUserData } from "services/fetch-change-data-user";
import { verifyUserEmail } from "services/fetch-change-user-email";
import { fetchCEP } from "services/fetch-cep";

export function LayoutUserData({
  profile,
}: Readonly<LayoutUserDateProps>) {
  const [showCpf, setShowCpf] = useState(false);
  const [modalPassword, setModalPassword] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [modalEmail, setModalEmail] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: profile.name,
      email: profile.email,
      phone: Formatter.prettyPhone(profile.phone),
      cpf: Formatter.prettyCPF(profile.cpf),
      birthdate: profile.birth_date.split("-").reverse().join("/"),
      password: "",
      cep: profile?.address?.cep || "",
      street: profile?.address?.street || "",
      number: profile?.address?.number || "",
      neighborhood: profile?.address?.neighborhood || "",
      city: profile?.address?.city || "",
      state: profile?.address?.state || "",
      complement: profile?.address?.complement || ""
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const phoneNumber = values.phone.replace(/\D/g, "");
      const dateOfBirth = Formatter.convertDateBrasilForEua(values.birthdate);

      const data = {
        name: values.name,
        phone: phoneNumber,
        birth_date: dateOfBirth,
        email: formik.values.email !== profile.email ? values.email : null,
        address: {
          street_name: values.street,
          street_number: values.number,
          neighborhood: values.neighborhood,
          city: values.city,
          state: values.state,
          complement: values.complement,
          cep: values.cep
        }
      };

      if (profile.id) {
        const res = handleChangeUserData(data, profile.id)

        toast.promise(res, {
          loading: "Salvando...",
          success: "Dados atualizados com sucesso",
          error: "Erro ao atualizar dados",
        });

        if (formik.values.email !== profile.email) {
          setModalEmail(false)
        }
      }
    }
  });

  const handleError = <T extends FormValues>(
    fieldName: keyof T,
    formik: any
  ) => {
    if (formik.touched[fieldName] && Boolean(formik.errors[fieldName])) {
      return `mt-1 border border-red-500 focus:ring-2 focus:outline-none focus:ring-red-500 focus:border-red-500`;
    }
    return `mt-1 border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`;
  };

  useEffect(() => {
    if (profile) {
      const hasChanged =
        formik.values.name !== profile.name ||
        formik.values.email !== profile.email ||
        formik.values.phone !== Formatter.prettyPhone(profile.phone) ||
        formik.values.cpf !== Formatter.prettyCPF(profile.cpf) ||
        formik.values.birthdate !==
          profile.birth_date.split("-").reverse().join("/") ||
          formik.values.street !== profile?.address?.street ||
          formik.values.number !== profile?.address?.number ||
          formik.values.neighborhood !== profile?.address?.neighborhood ||
          formik.values.city !== profile?.address?.city ||
          formik.values.state !== profile?.address?.state ||
          formik.values.complement !== profile?.address?.complement;

      setIsModified(hasChanged);
    }
  }, [
    formik.values,
    profile,
  ]);

  useEffect(() => {
    if (emailError) {
      formik.setFieldError('email', 'E-mail já está cadastrado');
    } else {
      setEmailError(false)
      formik.setFieldError('email', '');
    }
  }, [emailError]);

  const handleVerifyEmail = async () => {
    if (profile.id) {
      if (formik.values.email !== profile.email) {
        const emailExistsResponse =  await verifyUserEmail(formik.values.email, profile.id);
        
        if (emailExistsResponse === 200) {
          setModalEmail(true)
          setEmailError(false)
        } else {
          setModalEmail(false)
          setEmailError(true)
          return toast.error("O e-mail já está cadastrado no sistema.")
        }
      }
    }
  }

  const handleChangeUserData = async (data: ChangeUserData, id: string) => {
    try {
      const changeUser = await changeUserData(data, id)
      
      if (changeUser.detail) {
        return;
      }
  
      window.location.reload()
      return changeUser
    } catch (error) {
      console.error("Error fetching address data:", error);
    }
  }

  useEffect(() => {
    const handleFetchCep = async (cep: string) => {
      const cleanCep = cep.replace(/\D/g, '');
      if (cleanCep.length === 8) {
        try {
          const addressData: any = await fetchCEP(cleanCep);

          if (addressData === 404) {
            return toast.error("Erro ao buscar dados do endereço.");
          }

          formik.setValues({
            ...formik.values,
            street: addressData.street,
            neighborhood: addressData.neighborhood,
            city: addressData.city,
            state: addressData.state,
          });
        } catch (error) {
          console.error("Error fetching address data:", error);
          toast.error("Erro ao buscar dados do endereço.");
        }
      }
    };

    if (formik.values.cep && (profile.address?.cep !== formik.values.cep)) {
      handleFetchCep(formik.values.cep);
    }
  }, [formik.values.cep]);

  if (!profile) {
    return (
      <div>
        <h1>Carregando...</h1>
      </div>
    );
  }

  return (
    <section className="w-full h-full flex flex-col justify-start items-start">
      {modalPassword && (
        <ModalPassword setModalPassword={setModalPassword} />
      )}
      {modalEmail && (
        <ModalEmail setModalEmail={setModalEmail} formik={formik} />
      )}
      <div className="flex flex-col w-full px-1 md:px-4">
        <h1 className="text-2xl font-bold mb-4">{profile.name}</h1>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <form
          className="w-full"
          onSubmit={formik.handleSubmit}
        >
          <div className="w-full md:w-1/2 mt-8">
            <div className="mb-4">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
              >
                Nome completo
              </label>
              <input
                type="text"
                name="name"
                id="name"
                autoComplete="name"
                value={Formatter.prettyCapitalize(formik.values.name)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${handleError("name", formik)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formik.touched.name && formik.errors.name ? (
                <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
              ) : null}
            </div>
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
              >
                E-mail
              </label>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${handleError("email", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formik.touched.email && formik.errors.email ? (
                <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
              ) : null}
            </div>
            <div className="mb-4 relative">
              <label
                htmlFor="cpf"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
              >
                CPF
              </label>
              <input
                type={showCpf ? "text" : "password"}
                name="cpf"
                id="cpf"
                disabled
                value={Formatter.prettyCPF(formik.values.cpf)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${handleError("cpf", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formik.touched.cpf && formik.errors.cpf ? (
                <p className="text-red-500 text-xs mt-1">{formik.errors.cpf}</p>
              ) : null}
              {showCpf ? (
                <EyeOpen
                  className="absolute top-12 right-3 transform -translate-y-1/2 cursor-pointer text-neutral-500 dark:text-neutral-400"
                  onClick={() => setShowCpf(false)}
                />
              ) : (
                <EyeClosed
                  className="absolute top-12 right-3 transform -translate-y-1/2 cursor-pointer text-neutral-500 dark:text-neutral-400"
                  onClick={() => setShowCpf(true)}
                />
              )}
            </div>
            <div className="mb-4 flex gap-4 xl:w-[1209px] xl:flex-row w-full flex-col">
              <div>
                <label
                  htmlFor="cep"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  CEP
                </label>
                <input
                  type="text"
                  name="cep"
                  id="cep"
                  placeholder="xxxxx-xxx"
                  value={Formatter.prettyCEP(formik.values.cep)}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("cep", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.cep === '' ? (
                    <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.cep}</p>
                }
              </div>
              <div>
                <label
                  htmlFor="street"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  Rua
                </label>
                <input
                  type="text"
                  name="street"
                  id="street"
                  placeholder="Rua"
                  value={formik.values.street}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("street", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.street === '' ? (
                  <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.street}</p>
                }
              </div>
              <div>
                <label
                  htmlFor="number"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  Número
                </label>
                <input
                  type="text"
                  name="number"
                  id="number"
                  placeholder="123"
                  value={formik.values.number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("number", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.number === '' ? (
                  <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.number}</p>
                }
              </div>
              <div>
                  <label
                    htmlFor="complement"
                    className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                  >
                    Complemento
                  </label>
                  <input
                    type="text"
                    name="complement"
                    id="complement"
                    placeholder="Complemento"
                    value={formik.values.complement}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`${handleError("complement", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                  />
                  {formik.touched.complement && formik.errors.complement ? (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.complement}</p>
                  ) : null}
              </div>
            </div>
            <div className="mb-4 flex gap-4 lg:w-[898px] lg:flex-row w-full flex-col">
              <div>
                <label
                  htmlFor="neighborhood"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  Bairro
                </label>
                <input
                  type="text"
                  name="neighborhood"
                  id="neighborhood"
                  placeholder="Bairro"
                  value={formik.values.neighborhood}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("neighborhood", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.neighborhood === '' ? (
                  <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.neighborhood}</p>
                }
              </div>
              <div>
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  Cidade
                </label>
                <input
                  type="text"
                  name="city"
                  id="city"
                  placeholder="Cidade"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("city", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.city === '' ? (
                  <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.city}</p>
                }
              </div>
              <div>
                <label
                  htmlFor="state"
                  className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
                >
                  Estado
                </label>
                <input
                  type="text"
                  name="state"
                  id="state"
                  placeholder="Estado"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${handleError("state", formik)} disabled:bg-neutral-200 disabled:dark:bg-neutral-700 disabled:cursor-not-allowed block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                {formik.values.state === '' ? (
                  <p className="text-red-500 text-xs mt-1">Campo obrigatório</p>
                  ) : <p className="text-red-500 text-xs mt-1">{formik.errors.state}</p>
                }
              </div>
            </div>
            <div className="mb-4">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
              >
                Telefone
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                autoComplete="tel"
                value={Formatter.prettyPhone(formik.values.phone)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${handleError("phone", formik)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:rind-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formik.touched.phone && formik.errors.phone ? (
                <p className="text-red-500 text-xs mt-1">{formik.errors.phone}</p>
              ) : null}
            </div>
            <div className="mb-4">
              <label
                htmlFor="birthdate"
                className="block text-sm font-medium text-neutral-900 dark:text-neutral-300"
              >
                Data de nascimento
              </label>
              <input
                type="text"
                name="birthdate"
                id="birthdate"
                value={Formatter.prettyDateString(formik.values.birthdate)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`${handleError("birthdate", formik)} block w-full mt-2 px-3 py-2 border-neutral-300 dark:border-neutral-700 rounded-full shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              {formik.touched.birthdate && formik.errors.birthdate ? (
                <p className="text-red-500 text-xs mt-1">{formik.errors.birthdate}</p>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col w-full md:flex-row md:w-1/2 gap-4 md:items-center mt-8">
            <button
              type="button"
              className={`
                bg-neutral-200 hover:bg-neutral-400/60 dark:bg-neutral-700 
                hover:dark:bg-neutral-700/60 transition-colors duration-500 
                ease-in-out text-neutral-900 dark:text-neutral-200 px-6 
                py-2 rounded-full disabled:cursor-not-allowed disabled:bg-neutral-200
                disabled:hover:bg-neutral-200 dark:disabled:bg-neutral-700 
                dark:disabled:hover:bg-neutral-700
              `}
              onClick={() => formik.resetForm()}
              disabled={isModified ? false : true}
            >
              Cancelar
            </button>
            <button
              type={
                formik.values.email !== profile.email ? 'button' : 'submit'
              }
              onClick={async () => 
                formik.values.email !== profile.email
                  ? await handleVerifyEmail()
                  : null
              }
              className={`
                bg-[#4F46E5] hover:bg-[#4F46E5]/80 hover:dark:bg-[#4F46E5]/60 
                transition-colors duration-500 ease-in-out text-white 
                px-6 py-2 rounded-full disabled:cursor-not-allowed disabled:bg-[#4F46E5]
                disabled:hover:bg-[#4F46E5] dark:disabled:bg-[#4F46E5]
              `}
              disabled={isModified ? false : true}
            >
              Atualizar
            </button>
            <button
              type="button"
              className="bg-blue hover:bg-blue/80 md:ml-6 hover:dark:bg-blue/60 transition-colors duration-500 ease-in-out text-white px-6 py-2 rounded-full"
              onClick={() => setModalPassword(true)}
            >
              Alterar senha
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}
